import React from "react";
import styled from "styled-components";
import moment from "moment";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  AccentCard,
  CardHeader,
  FormRow,
  FormGroup,
  FormLabel,
  TextInput,
  SelectInput,
  Segmented,
  ActionRow,
  ChipRow,
  Chip,
  PrimaryButton,
} from "./socialLeadsStyles";

const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "inCrm", label: "In CRM" },
  { value: "notImported", label: "Not imported" },
  { value: "failed", label: "Failed" },
];

const QUICK_RANGES = [
  { days: 1, label: "Today" },
  { days: 4, label: "Last 4 days" },
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
];

/**
 * Filter card for the Meta leads screen.
 *
 * Controlled by the parent so filter state can live in the URL — a filtered
 * view is then shareable, which matters when someone is chasing a specific
 * missing enquiry.
 */
export const SocialLeadsFilters = ({
  filters,
  onChange,
  onApply,
  forms = [],
  employees = [],
  isFetching,
}) => {
  const setField = (field) => (event) =>
    onChange({ ...filters, [field]: event.target.value });

  const applyQuickRange = (days) => {
    const to = moment();
    const from = moment().subtract(days - 1, "days");
    onChange({
      ...filters,
      from: from.format("YYYY-MM-DD"),
      to: to.format("YYYY-MM-DD"),
    });
  };

  // A quick-range chip is "on" only while the dates still match what it sets,
  // so hand-editing either date clears the highlight rather than lying.
  const activeRange = QUICK_RANGES.find((range) => {
    const expectedTo = moment().format("YYYY-MM-DD");
    const expectedFrom = moment()
      .subtract(range.days - 1, "days")
      .format("YYYY-MM-DD");
    return filters.from === expectedFrom && filters.to === expectedTo;
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    onApply();
  };

  return (
    <AccentCard as="form" onSubmit={handleSubmit}>
      <CardHeader>
        <FilterAltOutlinedIcon />
        <h2>Filter</h2>
      </CardHeader>

      <Body>
        <FormRow>
          <FormGroup>
            <FormLabel htmlFor="meta-from">From</FormLabel>
            <TextInput
              id="meta-from"
              type="date"
              value={filters.from}
              max={filters.to || undefined}
              onChange={setField("from")}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="meta-to">To</FormLabel>
            <TextInput
              id="meta-to"
              type="date"
              value={filters.to}
              min={filters.from || undefined}
              onChange={setField("to")}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="meta-form">Form / Project</FormLabel>
            <SelectInput
              id="meta-form"
              value={filters.formId}
              onChange={setField("formId")}
            >
              <option value="">All forms</option>
              {forms.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.name}
                </option>
              ))}
            </SelectInput>
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="meta-emp">Assigned to</FormLabel>
            <SelectInput
              id="meta-emp"
              value={filters.assignedTo}
              onChange={setField("assignedTo")}
            >
              <option value="">Everyone</option>
              {employees.map((employee) => (
                <option key={employee._id} value={employee._id}>
                  {`${employee.firstName || ""} ${employee.lastName || ""}`.trim()}
                </option>
              ))}
            </SelectInput>
          </FormGroup>
        </FormRow>

        <FormRow>
          <FormGroup>
            <FormLabel htmlFor="meta-search">Name or phone</FormLabel>
            <TextInput
              id="meta-search"
              type="text"
              placeholder="Search submissions"
              value={filters.search}
              onChange={setField("search")}
            />
          </FormGroup>

          <FormGroup $span={2}>
            <FormLabel as="span" id="meta-status-label">
              Status
            </FormLabel>
            <Segmented role="group" aria-labelledby="meta-status-label">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.label}
                  type="button"
                  aria-pressed={filters.status === tab.value}
                  onClick={() => onChange({ ...filters, status: tab.value })}
                >
                  {tab.label}
                </button>
              ))}
            </Segmented>
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="meta-sort">Sort</FormLabel>
            <SelectInput
              id="meta-sort"
              value={filters.sort}
              onChange={setField("sort")}
            >
              <option value="newest">Newest submission first</option>
              <option value="oldest">Oldest submission first</option>
              <option value="action">Needs action first</option>
            </SelectInput>
          </FormGroup>
        </FormRow>

        <ActionRow>
          <ChipRow>
            {QUICK_RANGES.map((range) => (
              <Chip
                key={range.days}
                type="button"
                aria-pressed={activeRange?.days === range.days}
                onClick={() => applyQuickRange(range.days)}
              >
                {range.label}
              </Chip>
            ))}
          </ChipRow>

          <PrimaryButton type="submit" disabled={isFetching}>
            <SearchOutlinedIcon />
            {isFetching ? "Loading…" : "Apply filters"}
          </PrimaryButton>
        </ActionRow>
      </Body>
    </AccentCard>
  );
};

const Body = styled.div`
  padding: 15px 18px 16px 18px;
`;
